import { c as _c } from "react/compiler-runtime";
import type { FileDiffMetadata, FileDiffOptions, SupportedLanguages } from '@pierre/diffs';
import { parseDiffFromFile } from '@pierre/diffs';
import { FileDiff } from '@pierre/diffs/react';
import { PIERRE_DIFF_PALETTE_OVERRIDES, PIERRE_UNIFIED_DIFF_OPTIONS } from '@/components/shared/diff/pierre-diff-palette';
import { createPierreFileContents } from '@/lib/pr-v2/code-changes/pierre-file-contents';
export type PrV2ApplySuggestionPayload = {
  suggestion: string;
  originalLines?: string[];
};
type PrV2SuggestionBlockProps = {
  content: string;
  filePath?: string;
  originalLines?: string[];
  onApplySuggestion?: (payload: PrV2ApplySuggestionPayload) => Promise<void> | void;
  onApplySuggestionAndResolve?: (payload: PrV2ApplySuggestionPayload) => Promise<void> | void;
  applyDisabledReason?: string;
  isApplyingSuggestion?: boolean;
};
type NormalizedSuggestion = {
  oldLines: string[];
  newLines: string[];
  cleanedSuggestion: string;
  lineChangeMessage: string | null;
};
const SUGGESTION_FALLBACK_FILENAME = 'suggestion';
// A suggestion renders as a small two-file diff whose line numbers would restart at 1, so its rows
// carry +/- markers instead, and long unchanged runs keep their expandable bar.
const SUGGESTION_DIFF_OPTIONS: FileDiffOptions<undefined, undefined> = {
  ...PIERRE_UNIFIED_DIFF_OPTIONS,
  disableLineNumbers: true,
  diffIndicators: 'classic',
  hunkSeparators: 'line-info'
};
function trimTrailingBlanks(lines: string[]): string[] {
  let lastIdx = lines.length - 1;
  while (lastIdx >= 0 && lines[lastIdx] === '') {
    lastIdx -= 1;
  }
  return lines.slice(0, lastIdx + 1);
}
function buildSuggestionPreviewContents(lines: string[]): string {
  if (lines.length === 0) {
    return '';
  }
  return `${lines.join('\n')}\n`;
}

/**
 * GitHub commits a suggestion's text as written, but review models sometimes write it as a diff,
 * with a "+" or "-" on every line. Literal text often starts with "-" (Markdown bullets, YAML
 * lists, SQL comments), so without a "+" line the block reads as a diff only when its "-" lines are
 * the commented lines.
 */
function isMarkerFormatted(lines: string[], removedLines: string[], originalLines: string[] | undefined): boolean {
  const nonEmptyLines = lines.filter(line => line.length > 0);
  if (nonEmptyLines.length === 0 || !nonEmptyLines.every(line => line.startsWith('+') || line.startsWith('-'))) {
    return false;
  }
  return nonEmptyLines.some(line => line.startsWith('+')) || originalLines !== undefined && removedLines.length === originalLines.length && removedLines.every((line, index) => line === originalLines[index]);
}
function normalizeSuggestionContent(content: string, originalLines: string[] | undefined): NormalizedSuggestion {
  const rawLines = content.split(/\r?\n/).filter(line => line !== '\\ No newline at end of file');
  const removedLines = rawLines.filter(line => line.startsWith('-')).map(line => line.slice(1));
  const {
    oldLines,
    newLines
  } = isMarkerFormatted(rawLines, removedLines, originalLines) ? {
    oldLines: removedLines,
    newLines: trimTrailingBlanks(rawLines.filter(line => !line.startsWith('-')).map(line => line.startsWith('+') ? line.slice(1) : line))
  } : {
    oldLines: originalLines ?? [],
    newLines: trimTrailingBlanks(rawLines)
  };
  const diff = newLines.length - oldLines.length;
  const lineChangeMessage = diff !== 0 ? diff > 0 ? `+ ${diff} line${diff === 1 ? '' : 's'}` : `- ${Math.abs(diff)} line${Math.abs(diff) === 1 ? '' : 's'}` : null;
  return {
    oldLines,
    newLines,
    cleanedSuggestion: newLines.join('\n'),
    lineChangeMessage
  };
}
function buildSuggestionDiff(oldLines: string[], newLines: string[], filePath?: string): FileDiffMetadata | null {
  if (oldLines.length === 0 && newLines.length === 0) {
    return null;
  }
  const fileName = filePath?.trim() || SUGGESTION_FALLBACK_FILENAME;
  const language: SupportedLanguages | undefined = filePath ? undefined : 'text';
  try {
    return parseDiffFromFile(createPierreFileContents({
      name: fileName,
      contents: buildSuggestionPreviewContents(oldLines),
      role: 'old',
      lang: language
    }), createPierreFileContents({
      name: fileName,
      contents: buildSuggestionPreviewContents(newLines),
      role: 'new',
      lang: language
    }));
  } catch {
    return null;
  }
}
function LegacySuggestionRows(t0) {
  const $ = _c(7);
  const {
    oldLines,
    newLines
  } = t0;
  let t1;
  if ($[0] !== oldLines) {
    t1 = oldLines.map(_temp);
    $[0] = oldLines;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== newLines) {
    t2 = newLines.map(_temp2);
    $[2] = newLines;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div>{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function _temp2(line_0, i_0) {
  return <div key={`new-${line_0.length}-${i_0}`} className="bg-green-950/30 px-2.5 py-0.5 text-[12px] text-green-400"><span className="font-mono" style={{
      whiteSpace: "pre-wrap"
    }}>{`+${line_0}`}</span></div>;
}
function _temp(line, i) {
  return <div key={`old-${line.length}-${i}`} className="bg-red-950/30 px-2.5 py-0.5 text-[12px] text-red-400"><span className="font-mono" style={{
      whiteSpace: "pre-wrap"
    }}>{`-${line}`}</span></div>;
}
export function PrV2SuggestionBlock(t0) {
  const $ = _c(33);
  const {
    content,
    filePath,
    originalLines,
    onApplySuggestion,
    onApplySuggestionAndResolve,
    applyDisabledReason,
    isApplyingSuggestion: t1
  } = t0;
  const isApplyingSuggestion = t1 === undefined ? false : t1;
  let cleanedSuggestion;
  let lineChangeMessage;
  let newLines;
  let oldLines;
  let t2;
  if ($[0] !== content || $[1] !== filePath || $[2] !== originalLines) {
    ({
      oldLines,
      newLines,
      cleanedSuggestion,
      lineChangeMessage
    } = normalizeSuggestionContent(content, originalLines));
    t2 = buildSuggestionDiff(oldLines, newLines, filePath);
    $[0] = content;
    $[1] = filePath;
    $[2] = originalLines;
    $[3] = cleanedSuggestion;
    $[4] = lineChangeMessage;
    $[5] = newLines;
    $[6] = oldLines;
    $[7] = t2;
  } else {
    cleanedSuggestion = $[3];
    lineChangeMessage = $[4];
    newLines = $[5];
    oldLines = $[6];
    t2 = $[7];
  }
  const suggestionDiff = t2;
  const canApplySuggestion = Boolean(onApplySuggestion);
  const canApplySuggestionAndResolve = Boolean(onApplySuggestionAndResolve);
  const emptySuggestionReason = cleanedSuggestion.length === 0 ? "Cannot apply empty suggestion" : undefined;
  const effectiveApplyDisabledReason = applyDisabledReason ?? emptySuggestionReason;
  const isApplyDisabled = Boolean(effectiveApplyDisabledReason) || isApplyingSuggestion;
  const t3 = oldLines.length > 0 ? oldLines : undefined;
  let t4;
  if ($[8] !== cleanedSuggestion || $[9] !== t3) {
    t4 = {
      suggestion: cleanedSuggestion,
      originalLines: t3
    };
    $[8] = cleanedSuggestion;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const suggestionPayload = t4;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span>Suggested change</span>;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== lineChangeMessage) {
    t6 = lineChangeMessage ? <span className="text-zinc-500">{lineChangeMessage}</span> : null;
    $[12] = lineChangeMessage;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t6) {
    t7 = <div className="flex items-center justify-between bg-zinc-800/50 px-2.5 py-1.5 text-[11px] text-zinc-400">{t5}{t6}</div>;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== newLines || $[17] !== oldLines || $[18] !== suggestionDiff) {
    t8 = suggestionDiff ? <FileDiff fileDiff={suggestionDiff} options={SUGGESTION_DIFF_OPTIONS} style={PIERRE_DIFF_PALETTE_OVERRIDES} className="block" /> : <LegacySuggestionRows oldLines={oldLines} newLines={newLines} />;
    $[16] = newLines;
    $[17] = oldLines;
    $[18] = suggestionDiff;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== canApplySuggestion || $[21] !== canApplySuggestionAndResolve || $[22] !== effectiveApplyDisabledReason || $[23] !== isApplyDisabled || $[24] !== isApplyingSuggestion || $[25] !== onApplySuggestion || $[26] !== onApplySuggestionAndResolve || $[27] !== suggestionPayload) {
    t9 = canApplySuggestion ? <div className="flex justify-end gap-2 border-t border-zinc-700/40 bg-zinc-900/30 px-2.5 py-2">{canApplySuggestionAndResolve ? <button type="button" className="rounded-sm bg-zinc-900 px-2.5 py-1 text-[11px] text-zinc-300 hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50" disabled={isApplyDisabled} title={effectiveApplyDisabledReason} onClick={() => {
        if (!onApplySuggestionAndResolve) {
          return;
        }
        onApplySuggestionAndResolve(suggestionPayload);
      }}>{isApplyingSuggestion ? "Applying\u2026" : "Apply and resolve"}</button> : null}<button type="button" className="rounded-sm bg-zinc-800 px-2.5 py-1 text-[11px] text-zinc-300 hover:bg-zinc-700 disabled:cursor-not-allowed disabled:opacity-50" disabled={isApplyDisabled} title={effectiveApplyDisabledReason} onClick={() => {
        if (!onApplySuggestion) {
          return;
        }
        onApplySuggestion(suggestionPayload);
      }}>{isApplyingSuggestion ? "Applying\u2026" : "Apply suggestion"}</button></div> : null;
    $[20] = canApplySuggestion;
    $[21] = canApplySuggestionAndResolve;
    $[22] = effectiveApplyDisabledReason;
    $[23] = isApplyDisabled;
    $[24] = isApplyingSuggestion;
    $[25] = onApplySuggestion;
    $[26] = onApplySuggestionAndResolve;
    $[27] = suggestionPayload;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== t7 || $[30] !== t8 || $[31] !== t9) {
    t10 = <div className="mt-2 overflow-hidden rounded-md border border-zinc-700/40">{t7}{t8}{t9}</div>;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  return t10;
}